import EditIcon from "@mui/icons-material/Edit";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import {
  Alert,
  Box,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import {
  AlertsContext,
  DistaContent,
  DistaPageHeaderProps,
} from "@sista/dista-ui";
import { SistaPageLayout } from "@sista/fe-commons";
import { IdmClientContext } from "@sista/idm-client";
import {
  ProfileRepresentation,
  UiContext,
  useSafeSistaAsync,
} from "@sista/library-stasi";
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useRejstrikClient } from "../../api/utils/useRejstrikClient";
import { useCanManageRepresentations } from "../../hooks/useCanManageRepresentations";
import { useLocalNavigation } from "../../hooks/useLocalNavigation";
import {
  formatRepresentationPeriod,
  pendingRepresentationState,
  urnEntityId,
} from "../../utils/representation";
import { ChangeRepresentationDialog } from "../dialogs/representation/ChangeRepresentationDialog";

/**
 * Zastupy zamestnancu poskytovatele: spravce poskytovatele (pravo rejstrik:representation-manage)
 * pro svoje zamestnance, admin SISTA pro libovolneho poskytovatele.
 */
export const RepresentationsAdminScreen = () => {
  const { loggedAccount } = useContext(IdmClientContext);
  const { confirm, openDialog, openedDialog, openedDialogData } =
    useContext(UiContext);
  const { showSuccess } = useContext(AlertsContext);
  const { t } = useTranslation("ucet");
  const rej = useRejstrikClient();
  const navigate = useNavigate();
  const params = useParams();
  const providerId = params.providerId ?? loggedAccount.providerId;
  const canManage = useCanManageRepresentations(providerId);
  const admin = loggedAccount.admin === true;

  const [providersCall, providersState] = useSafeSistaAsync(
    rej.findAllProviders
  );
  useEffect(() => {
    if (admin) providersCall({});
  }, [admin, providersCall]);

  const [listCall, listState] = useSafeSistaAsync(
    rej.findProviderRepresentations
  );
  const refresh = useCallback(() => {
    if (canManage && providerId) listCall({ providerId });
  }, [canManage, providerId, listCall]);
  useEffect(refresh, [refresh]);

  const [endCall] = useSafeSistaAsync(
    useCallback(
      async (r: ProfileRepresentation) => {
        const yes = await confirm({
          question: r.pending
            ? t(
                "ucet.representation.cancelRequestConfirmationOf",
                "Opravdu zrušit žádost o zástup za {{name}}?",
                { name: r.profileDisplayName }
              )
            : t(
                "ucet.representation.endConfirmationOf",
                "Opravdu ukončit zástup za {{name}}?",
                { name: r.profileDisplayName }
              ),
        });
        if (!yes) return;
        await rej.endRepresentation({ profileId: urnEntityId(r.profileUrn) });
        showSuccess(
          r.pending
            ? t(
                "ucet.representation.requestCanceled",
                "Žádost o zástup byla zrušena"
              )
            : t("ucet.representation.ended", "Zástup byl ukončen")
        );
        refresh();
      },
      [confirm, rej, showSuccess, t, refresh]
    )
  );

  const columns = useMemo(
    (): GridColDef<ProfileRepresentation>[] => [
      {
        field: "profileDisplayName",
        headerName: t("ucet.representation.table.represented", "Zastupovaný"),
        flex: 1,
      },
      {
        field: "substituteDisplayName",
        headerName: t("ucet.representation.table.substitute", "Zástupce"),
        flex: 1,
      },
      {
        field: "period",
        headerName: t("ucet.representation.table.period", "Termín"),
        width: 220,
        valueGetter: (p) => formatRepresentationPeriod(p.row),
      },
      {
        field: "running",
        headerName: t("ucet.representation.table.state", "Stav"),
        width: 150,
        renderCell: (p) => (
          <Typography
            variant="body1"
            color={
              p.row.pending
                ? "text.secondary"
                : p.row.running
                ? "primary.main"
                : "warning.main"
            }
          >
            {p.row.pending
              ? pendingRepresentationState(t, p.row)
              : p.row.running
              ? t("ucet.representation.running", "Probíhá")
              : t("ucet.representation.planned", "Naplánován")}
          </Typography>
        ),
      },
      {
        field: "actions",
        type: "actions",
        width: 100,
        // rozpracovanou zadost (workflow) jde jen zrusit, menit az vznikly zastup
        getActions: (p) => [
          ...(p.row.pending
            ? []
            : [
                <GridActionsCellItem
                  key="change"
                  icon={<EditIcon />}
                  label={t("ucet.representation.change", "Změnit zástup")}
                  onClick={() => openDialog("changeRepresentation", p.row)}
                />,
              ]),
          <GridActionsCellItem
            key="end"
            icon={<StopCircleIcon />}
            label={
              p.row.pending
                ? t("ucet.representation.cancelRequest", "Zrušit žádost")
                : t("ucet.representation.end", "Ukončit zástup")
            }
            onClick={() => endCall(p.row)}
          />,
        ],
      },
    ],
    [t, openDialog, endCall]
  );

  const headerConfig: DistaPageHeaderProps = {
    breadcrumbs: [
      {
        title: t("ucet.menu.breadcrumbs.home", "Domů"),
        action: () => (window.location.href = "/ucet/"),
      },
      {
        title: t("ucet.representation.adminTitle", "Zástupy zaměstnanců"),
      },
    ],
    pageTitle: t("ucet.representation.adminTitle", "Zástupy zaměstnanců"),
    chipsDefinition: [],
    actions: [],
  };

  const localNavigationConfig = useLocalNavigation();

  return (
    <SistaPageLayout localNavigationConfig={localNavigationConfig}>
      <DistaContent headerConfig={headerConfig} fullWidth={true}>
        <Stack
          direction="column"
          className="height-fullscreen"
          sx={{ width: "100%" }}
          spacing={2}
        >
          {admin && (
            <TextField
              select
              sx={{ maxWidth: 400, m: 2 }}
              label={t("ucet.representation.provider", "Poskytovatel")}
              value={providerId ?? ""}
              onChange={(e) => navigate("/zastupy/" + e.target.value)}
            >
              {(providersState.value ?? []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.displayName}
                </MenuItem>
              ))}
            </TextField>
          )}
          {!canManage ? (
            <Alert severity="info" sx={{ m: 2 }}>
              {t(
                "ucet.representation.noPermission",
                "Zástupy zaměstnanců může spravovat jen administrátor poskytovatele."
              )}
            </Alert>
          ) : (
            <Box sx={{ flexGrow: 1 }}>
              <DataGrid
                columns={columns}
                rows={listState.value ?? []}
                getRowId={(r) => r.processId ?? r.profileUrn}
                loading={listState.pending}
                pageSizeOptions={[100]}
              />
            </Box>
          )}
        </Stack>
        {openedDialog == "changeRepresentation" && (
          <ChangeRepresentationDialog
            representation={openedDialogData}
            onDone={refresh}
          />
        )}
      </DistaContent>
    </SistaPageLayout>
  );
};
